<script lang="ts">
	import { browser } from '$app/environment';
	import { afterNavigate } from '$app/navigation';
	import { base } from '$app/paths';
	import { page } from '$app/stores';
	import { prefetchPreAuth } from '@infinitro/auth';
	import { Background } from '@infinitro/background';
	import { BetBy } from '@infinitro/betby';
	import { initGamesState } from '@infinitro/catalog-ex';
	import { getProviders } from '@infinitro/catalog-min';
	import {
		cfgAppVersion,
		cfgBetbyScriptUrl,
		cfgBrandTitle,
		cfgCanonicalUrl,
		cfgEventThemeCdnPath,
		cfgNoIndex,
		cfgOptimoveTenantId,
		cfgOptimoveTenantToken,
		cfgToThemeCdnPath,
		FeatureSet,
		isEventAssetEnabled,
		ReleaseStage,
		stateFeatureSet,
		stateHasSports,
		stateRealm,
		stateTheme,
		stateUi,
		wantAtLeastReleaseStage
	} from '@infinitro/config';
	import { seoContent, seoContentReady } from '@infinitro/content';
	import { CookiesModal } from '@infinitro/cookies';
	import { Footer } from '@infinitro/footer';
	import {
		Fonts,
		handleModalRouteNavigation,
		handlePublicToPlayerRedirect,
		hasSportsLoaded,
		initModalRouteDismissSync,
		isBetbyDisplayed,
		isFullScreenGame,
		isMobile,
		isPlayerChatOpen,
		loggedIn,
		ModalDialog,
		selectedItemMobileMenu,
		sidebarCollapsed,
		stateIFrameHref,
		stateOpenModal,
		stateShowSidebarMobile,
		syncLocaleFromPathname
	} from '@infinitro/global';
	import { Header } from '@infinitro/header-public';
	import { Iframe } from '@infinitro/iframe';
	import { MobileMenu } from '@infinitro/mobile-menu';
	import { PublicNavigation } from '@infinitro/navigation';
	import { setProviders, setTitleAndDescriptionForSEO } from '@infinitro/seo';
	import { SideBar } from '@infinitro/sidebar';
	import { SupportChat } from '@infinitro/support-chat';
	import { ToastNotifications } from '@infinitro/toast-notifications';
	import { stateVisit } from '@infinitro/visit';
	import { onMount } from 'svelte';
	import { get } from 'svelte/store';
	import '../app.postcss';
	import Modals from './modals.svelte';

	let realm: string;
	stateRealm.subscribe((value) => (realm = value));

	const APP_VERSION = cfgAppVersion();
	const BETBY_SCRIPT_URL = cfgBetbyScriptUrl();
	const CANONICAL_URL = cfgCanonicalUrl();

	const webSiteOrganizationLdJson = (): string =>
		JSON.stringify({
			'@context': 'https://schema.org',
			'@type': 'WebSite',
			name: cfgBrandTitle(),
			url: CANONICAL_URL
		});
	// Also noindex /ko/* on fomo.io — Korean content lives on kr.fomo.io subdomain
	$: NO_INDEX =
		cfgNoIndex() || (CANONICAL_URL === 'https://fomo.io' && $page.url.pathname.startsWith('/ko/'));
	// Locale-prefixed game/provider pages — crawlable but noindex'd to avoid duplicate CSR shells.
	// Sports locale pages are excluded: static shells are generated per locale and are indexable.
	$: isLocaleGamePage =
		/^\/(?:en|cs|de|es|fr|it|ja|ko|pt|ru|zh)\/(?:p|providers)\//.test($page.url.pathname) ||
		/^\/v1(\/|$)/.test($page.url.pathname);
	const FAVICON_PATH = cfgToThemeCdnPath('/favicons');
	const OG_IMAGE_URL = cfgToThemeCdnPath('/og/og-image.jpg');

	// Routes shared between fomo.io and kr.fomo.io — require hreflang alternates
	const HREFLANG_GLOBAL = 'https://fomo.io';
	const HREFLANG_KR = 'https://kr.fomo.io';
	const HREFLANG_ROUTES = new Set([
		'/',
		'/casino/',
		'/sports/',
		'/livecasino/',
		'/slots/',
		'/vip/',
		'/welcome-offer/',
		'/referral/',
		'/promotion-page/'
	]);
	const hreflangEnabled = CANONICAL_URL === HREFLANG_GLOBAL || CANONICAL_URL === HREFLANG_KR;
	const OG_LOCALE_MAP: Record<string, string> = {
		de: 'de_DE',
		es: 'es_ES',
		fr: 'fr_FR',
		it: 'it_IT',
		pt: 'pt_BR',
		ru: 'ru_RU',
		cs: 'cs_CZ',
		ja: 'ja_JP',
		ko: 'ko_KR',
		zh: 'zh_CN'
	};

	console.log(`public website version ${APP_VERSION}`);

	let showSidebarMobile: boolean = false;
	let playerChatOpen: boolean = false;

	const setAppViewportHeight = () => {
		const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
		const next = `${viewportHeight}px`;
		const prev = document.documentElement.style.getPropertyValue('--app-vh').trim();
		if (prev === next) return;
		document.documentElement.style.setProperty('--app-vh', next);
	};

	const blurActiveEditable = () => {
		const activeElement = document.activeElement;
		if (!(activeElement instanceof HTMLElement)) return;
		const tag = activeElement.tagName;
		const isEditable =
			activeElement.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
		if (isEditable) activeElement.blur();
	};

	const waitForKeyboardToSettle = async () => {
		const vv = window.visualViewport;
		const baselineHeight = window.innerHeight;
		const getViewportHeight = () => vv?.height ?? window.innerHeight;
		const sleepFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

		blurActiveEditable();
		setAppViewportHeight();

		if (!vv) {
			await sleepFrame();
			setAppViewportHeight();
			return;
		}

		const maxDeadline = performance.now() + 450;
		let previousHeight = getViewportHeight();

		while (performance.now() < maxDeadline) {
			await sleepFrame();
			const currentHeight = getViewportHeight();
			const keyboardLikelyOpen = currentHeight < baselineHeight - 120;
			const hasStabilized = Math.abs(currentHeight - previousHeight) < 1;
			setAppViewportHeight();
			if (!keyboardLikelyOpen && hasStabilized) break;
			previousHeight = currentHeight;
		}

		setAppViewportHeight();
		setTimeout(setAppViewportHeight, 120);
	};

	stateShowSidebarMobile.subscribe((value) => (showSidebarMobile = value));
	isPlayerChatOpen.subscribe((value) => (playerChatOpen = value));
	stateVisit.subscribe((value) => {
		if (value) console.log(`visit ${value}`);
	});

	let isLoggedIn: boolean | undefined = undefined;
	loggedIn.subscribe((value) => {
		if (value === isLoggedIn) return;
		isLoggedIn = value;
		if (isLoggedIn) {
			handlePublicToPlayerRedirect();
		}

		initGamesState(value ?? false, { realm });
	});

	const onCollapseSidebar = () => {
		if (window.innerWidth < 1024) return;
		sidebarCollapsed.update((value) => !value);
	};

	const onShowSidebar = () => {
		const shouldOpen = !showSidebarMobile;

		if (shouldOpen) {
			isPlayerChatOpen.set(false);
			stateShowSidebarMobile.set(true);
			waitForKeyboardToSettle().then(() => {
				if (!showSidebarMobile) return;
				window.scrollTo(0, 0);
				document.documentElement.scrollTop = 0;
				document.body.scrollTop = 0;
				innerPageElement?.scrollTo({ top: 0, behavior: 'auto' });
			});
		} else {
			stateShowSidebarMobile.set(false);
		}
	};

	const onShowPlayerChat = () => {
		isPlayerChatOpen.set(!playerChatOpen);
	};

	const onShowSearchModal = () => {
		stateOpenModal.set(ModalDialog.Search);
	};

	const onMobileMenuChatSlotClick = () => {
		if ($isMobile && $isFullScreenGame && $stateUi.games.showNavigationOnMobile) {
			isPlayerChatOpen.set(false);
			stateShowSidebarMobile.set(false);
			return;
		}
		onShowPlayerChat();
	};

	let innerPageElement: HTMLElement;

	afterNavigate(() => {
		const currentUrl = get(page).url;
		const pathname = currentUrl.pathname;
		syncLocaleFromPathname(pathname);
		handleModalRouteNavigation(currentUrl, base);
		const langMatch = pathname.match(/^\/([a-z]{2})(\/|$)/);
		document.documentElement.lang = langMatch ? langMatch[1] : 'en';
		if (innerPageElement) {
			innerPageElement.scrollTo({
				top: 0,
				behavior: 'smooth'
			});
		}
	});

	onMount(() => {
		const stopModalRouteDismissSync = initModalRouteDismissSync(() => get(page).url, base);
		handleModalRouteNavigation(get(page).url, base);

		// Remove the SSG pre-rendered markdown div — crawlers see it in static HTML, users get
		// the properly-styled CSR SeoText instead once the app boots.
		document.getElementById('seo-prerender')?.remove();

		// Set lang at initial hydration — covers English HTML served at non-English locale paths.
		// afterNavigate handles subsequent navigations; this fires before it on first mount.
		const initPathname = get(page).url.pathname;
		const initLangMatch = initPathname.match(/^\/([a-z]{2})(\/|$)/);
		document.documentElement.lang = initLangMatch ? initLangMatch[1] : 'en';

		if (window.top && window !== window.top) {
			window.top.location = window.location;
		}

		if (!get(loggedIn)) {
			prefetchPreAuth();
		}

		setAppViewportHeight();
		requestAnimationFrame(setAppViewportHeight);
		setTimeout(setAppViewportHeight, 150);
		window.addEventListener('resize', setAppViewportHeight);
		window.addEventListener('orientationchange', setAppViewportHeight);
		window.visualViewport?.addEventListener('resize', setAppViewportHeight);
		window.visualViewport?.addEventListener('scroll', setAppViewportHeight);
		document.addEventListener('focusout', setAppViewportHeight);

		// Set event theme page background if active
		if (innerPageElement && isEventAssetEnabled('pageBackground')) {
			const pageBgPath = cfgEventThemeCdnPath('pagebg.png');
			if (pageBgPath) {
				innerPageElement.style.setProperty('--event-page-bg', `url('${pageBgPath}')`);
			}
		}

		getProviders().then((result) => {
			setProviders(result);
			// Re-run SEO after providers loaded for dynamic game/provider titles
			setTitleAndDescriptionForSEO($page.url.pathname, $page.data);
		});

		let wasInRange: boolean | null = null;

		const handleResize = () => {
			const width = window.innerWidth;

			if (width < 1024) {
				sidebarCollapsed.set(false);
				wasInRange = false;
				return;
			}

			const isInRange = width >= 1024 && width < 1280;

			if (isInRange && wasInRange !== true) {
				sidebarCollapsed.set(true);
			} else if (!isInRange && wasInRange === true && width >= 1280) {
				sidebarCollapsed.set(false);
			}

			wasInRange = isInRange;
		};

		handleResize();

		window.addEventListener('resize', handleResize);

		return () => {
			stopModalRouteDismissSync();
			window.removeEventListener('resize', setAppViewportHeight);
			window.removeEventListener('orientationchange', setAppViewportHeight);
			window.visualViewport?.removeEventListener('resize', setAppViewportHeight);
			window.visualViewport?.removeEventListener('scroll', setAppViewportHeight);
			document.removeEventListener('focusout', setAppViewportHeight);
			window.removeEventListener('resize', handleResize);
		};
	});

	$: {
		if (!showSidebarMobile) {
			stateShowSidebarMobile.set(false);
		}
	}
	$: canonicalUrl = (() => {
		const p = $page.url.pathname;
		// Strip locale prefix for game/provider pages — canonical is always the unprefixed /p/ URL
		const localeGameMatch = p.match(
			/^\/(?:en|cs|de|es|fr|it|ja|ko|pt|ru|zh)(\/(?:p|providers)\/.+)$/
		);
		if (localeGameMatch) return new URL(localeGameMatch[1], CANONICAL_URL).href;
		// /en/* pages canonicalize to the unprefixed root URL to avoid competing with /
		const enMatch = p.match(/^\/en(\/.*)?$/);
		const effectivePath = enMatch ? enMatch[1] || '/' : p;
		return new URL(effectivePath, CANONICAL_URL).href;
	})();
	$: hreflangPath = (() => {
		if (!hreflangEnabled) return null;
		const p = $page.url.pathname;
		const normalized = p.endsWith('/') ? p : p + '/';
		return HREFLANG_ROUTES.has(normalized) ? normalized : null;
	})();
	$: ogLocale = (() => {
		const langMatch = $page.url.pathname.match(/^\/([a-z]{2})(\/|$)/);
		return langMatch ? (OG_LOCALE_MAP[langMatch[1]] ?? 'en_US') : 'en_US';
	})();
	$: isBackdropVisibleDesktop = (!$sidebarCollapsed || playerChatOpen) && !$isMobile;
	$: isBackdropVisibleMobile = (showSidebarMobile || playerChatOpen) && $isMobile;

	$: if (browser && $seoContentReady) {
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		$seoContent; // reactive dependency — re-runs when locale SEO content changes
		setTitleAndDescriptionForSEO($page.url.pathname, $page.data);
	}

	$: isSportsRoute = $page.url.pathname.includes('/sports');
	$: if (browser) {
		document.documentElement.setAttribute('data-theme', $stateTheme);
		document.documentElement.classList.toggle('sports-route', isSportsRoute);
	}
	$: showHeader =
		!$isFullScreenGame ||
		($isMobile &&
			$isFullScreenGame &&
			$stateUi.games.showNavigationOnMobile &&
			$selectedItemMobileMenu !== 'chat');
	$: useAlternative4 = $stateUi.layout.layoutStyle === 'alternative4';
</script>

<svelte:head>
	<meta http-equiv="X-Frame-Options" content="DENY" />
	<meta property="og:type" content="website" />
	<meta property="og:locale" content={ogLocale} />
	<meta name="twitter:card" content="summary_large_image" />
	<meta property="og:image" content={OG_IMAGE_URL} />
	<meta name="twitter:image" content={OG_IMAGE_URL} />
	<link rel="canonical" href={canonicalUrl} />
	<meta property="og:url" content={canonicalUrl} />
	<meta name="twitter:url" content={canonicalUrl} />
	{#if NO_INDEX}
		<meta name="robots" content="noindex, nofollow" />
	{:else if isLocaleGamePage}
		<meta name="robots" content="noindex, follow" />
	{:else}
		<meta name="robots" content="index,follow,max-image-preview:large" />
	{/if}
	{#if hreflangPath}
		<link rel="alternate" hreflang="x-default" href="{HREFLANG_GLOBAL}{hreflangPath}" />
		<link rel="alternate" hreflang="en" href="{HREFLANG_GLOBAL}{hreflangPath}" />
		<link rel="alternate" hreflang="ko" href="{HREFLANG_KR}{hreflangPath}" />
	{/if}
	{#if $stateUi.layout.embedWebSiteOrganizationJsonLd}
		<meta property="og:site_name" content={cfgBrandTitle()} />
		{@html '<script type="application/ld+json">' + webSiteOrganizationLdJson() + '</script>'}
	{/if}

	{#if BETBY_SCRIPT_URL && $hasSportsLoaded}
		<script src={BETBY_SCRIPT_URL}></script>
	{/if}

	<link rel="icon" href={`${FAVICON_PATH}/favicon.ico`} sizes="48x48" />
	<link rel="icon" type="image/png" sizes="32x32" href={`${FAVICON_PATH}/favicon-32x32.png`} />
	<link rel="apple-touch-icon" sizes="180x180" href={`${FAVICON_PATH}/apple-touch-icon.png`} />
</svelte:head>

<PublicNavigation />
<Fonts />

<!-- <iframe class="fullpage" src="https://www.youtube.com/embed/1w7OgIMMRc4" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen /> -->

<div class="page" class:betby-layout={$isBetbyDisplayed} class:page-alternative-4={useAlternative4}>
	{#if $stateIFrameHref && wantAtLeastReleaseStage(ReleaseStage.Gamma)}
		<Iframe />
	{/if}
	<Modals />
	{#if showHeader}
		<div class="header">
			<Header {onShowPlayerChat} />
		</div>
	{/if}
	<div
		class:h-full={$isFullScreenGame}
		class:betby-layout={$isBetbyDisplayed}
		class="outerpage"
		style={`--right-panel-width: ${
			playerChatOpen ? 'var(--chat-width)' : '0px'
		}; --sidebar-width: ${$sidebarCollapsed ? '72px' : '240px'}`}
	>
		<div
			class="sidebar"
			class:sports-route={isSportsRoute}
			class:shell-sidebar-tablet-alt-bg={$stateUi.layout.useAlternateMobileSidebarBackdrop &&
				$isMobile &&
				showSidebarMobile}
		>
			<SideBar
				{onCollapseSidebar}
				withoutTopBorder={true}
				useParentState={true}
				isCollapsed={$sidebarCollapsed}
				bind:isOpen={showSidebarMobile}
			/>
		</div>
		{#if isBackdropVisibleDesktop || isBackdropVisibleMobile}
			<div
				class="backdrop"
				on:click={() => {
					if (!$isMobile) {
						sidebarCollapsed.set(true);
						isPlayerChatOpen.set(false);
					}
				}}
			></div>
		{/if}
		<div
			class:innerpage-full={$isFullScreenGame}
			class:event-theme={isEventAssetEnabled('pageBackground')}
			class:sports-route={isSportsRoute}
			class="innerpage {$sidebarCollapsed ? 'with-sidebar-collapsed' : ''} {playerChatOpen &&
			!$sidebarCollapsed
				? 'with-notifications-open'
				: ''}"
			bind:this={innerPageElement}
		>
			{#if $stateHasSports && isSportsRoute}
				<div class="inner visible">
					<BetBy />
				</div>
			{/if}
			<!-- <iframe class="inner" src="https://www.youtube.com/embed/1w7OgIMMRc4" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen /> -->
			<slot />
			{#if !$isBetbyDisplayed && !isSportsRoute}
				<Footer />
			{/if}
		</div>

		{#if playerChatOpen}
			<div class="chat-notifications"></div>
		{/if}
	</div>
	<Background
		shellLayout
		customStyle={'top: calc(70px + env(safe-area-inset-top, 0px)); width: auto; height: auto;'}
	/>
	<MobileMenu
		onClickMenu={onShowSidebar}
		onClickChat={onMobileMenuChatSlotClick}
		onClickSearch={onShowSearchModal}
	/>
	<ToastNotifications />
	{#if $stateFeatureSet === FeatureSet.Preview && wantAtLeastReleaseStage(ReleaseStage.Alpha)}
		<SupportChat shellEmbed />
	{/if}
	<CookiesModal />
</div>

<style lang="postcss">
	:global(body) {
		@apply [font-family:var(--font-main)];
		scroll-behavior: smooth;
	}

	:global(html.sports-route),
	:global(html.sports-route body) {
		overscroll-behavior: none;
	}

	:global(.fixed-loading-screen) {
		position: fixed !important;
		z-index: 64000 !important;
	}

	.header {
		@apply fixed z-[60] top-0 left-0 right-0;
		padding-top: env(safe-area-inset-top);
		padding-left: env(safe-area-inset-left);
		padding-right: env(safe-area-inset-right);
	}

	.outerpage {
		@apply flex flex-row w-full overflow-hidden;
		height: calc(
			var(--app-vh, 100vh) - 72px - env(safe-area-inset-top) - env(safe-area-inset-bottom, 0px)
		);
		height: calc(100dvh - 72px - env(safe-area-inset-top) - env(safe-area-inset-bottom, 0px));
		margin-top: calc(72px + env(safe-area-inset-top));
	}
	.outerpage.betby-layout {
		overflow: visible;
		overscroll-behavior: none;
	}

	@media (max-width: 1023px) {
		.outerpage.betby-layout {
			margin-top: 0;
			height: var(--app-vh, 100vh);
			height: 100dvh;
		}
	}

	.sidebar {
		@apply static w-fit z-40 h-full;
	}

	@media (max-width: 1023px) {
		.page .sidebar {
			@apply fixed;
			top: 0;
			left: 0;
			width: 0;
		}
	}

	@media (min-width: 1024px) {
		.page .sidebar.sports-route {
			@apply fixed;
			top: calc(72px + env(safe-area-inset-top));
			left: 0;
			width: var(--sidebar-width, 0px);
			height: calc(
				100dvh - 72px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)
			);
		}
	}

	.shell-sidebar-tablet-alt-bg {
		@apply bg-[var(--color-background-900)];
	}

	.chat-notifications {
		@apply static z-40 h-full;
		width: var(--right-panel-width, 0px);
	}

	@media (max-width: 1023px) {
		.page .chat-notifications {
			@apply fixed;
			width: 0;
		}
	}

	.page {
		@apply flex flex-col z-10 h-screen w-full overflow-hidden;
		height: 100dvh;
	}
	.page.betby-layout {
		overflow: visible;
		overscroll-behavior: none;
	}

	.innerpage {
		@apply relative w-full z-10 flex flex-col justify-between;
		width: 100%;
		overflow-y: auto;
		overflow-x: hidden;
		align-items: center;

		@media (min-width: 1024px) {
			/* Only apply calculation on desktop */
			width: calc(100% - var(--sidebar-width, 0px) - var(--right-panel-width, 0px));
		}

		&::-webkit-scrollbar {
			@apply w-[8px];
		}

		&::-webkit-scrollbar-track {
			@apply bg-transparent;
		}

		&::-webkit-scrollbar-thumb {
			@apply bg-[var(--color-secondary-500)] rounded-full;
		}
	}

	.innerpage.sports-route {
		overflow: visible;
		overscroll-behavior: none;
	}

	@media (min-width: 1024px) {
		.innerpage.sports-route {
			margin-left: var(--sidebar-width, 0px);
			width: calc(100% - var(--sidebar-width, 0px) - var(--right-panel-width, 0px));
		}
	}

	.innerpage.event-theme::before {
		content: '';
		position: fixed;
		top: calc(72px + env(safe-area-inset-top, 0px));
		left: 0;
		right: 0;
		bottom: 0;
		width: 100%;
		height: calc(
			var(--app-vh, 100vh) - 72px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)
		);
		height: calc(100dvh - 72px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px));
		background-image: var(--event-page-bg);
		background-size: auto;
		background-position: top left;
		background-repeat: repeat;
		opacity: 0.05;
		z-index: 0;
		pointer-events: none;
	}

	.innerpage-full {
		@apply w-full;
	}

	.with-sidebar-collapsed {
		--sidebar-width: 72px;
	}

	.fullpage {
		@apply w-full h-full absolute bottom-0 right-0 top-0 left-0 z-40;
	}

	.inner {
		@apply w-full min-w-0 max-w-full min-h-[calc(100vh-64px)] relative bottom-0 right-0 top-0 left-0 z-40 flex flex-col;
	}

	.hidden {
		@apply invisible h-0 min-h-0 overflow-hidden;
	}

	.none {
		@apply hidden;
	}

	.h-full {
		@apply !h-screen !mt-0;
	}

	.page .backdrop {
		@apply fixed inset-0 z-30 bg-black bg-opacity-50 w-full h-full;
		display: none;
	}

	@screen md {
		@media (max-width: 1279px) {
			.page .innerpage {
				@apply w-full;
			}

			.page .sidebar {
				@apply fixed;
			}

			.page .backdrop {
				@apply block;
			}

			.page .chat-notifications {
				@apply fixed;
			}

			.page .with-notifications-open {
				--right-panel-width: 0;
			}
		}
	}

	@screen lg {
		@media (max-width: 1279px) {
			.page .innerpage {
				@apply ml-[72px];
			}
		}
	}

	.page-alternative-4 {
		background: var(--component-layout-body-bg);
	}
</style>
